/**
 * lib/analytics — pure derivations that turn the raw API rows (trades, decisions, the
 * equity curve) into the aggregates the Analytics page charts. Everything here is a
 * total function of its inputs (no fetching, no time-of-day, no `Date.now`), so the same
 * rows always produce the same figures — cheap to test and safe to memoize.
 *
 * All P&L is expressed in **R multiples** (risk units), because the trade_memories row is
 * a learning record and carries realized R (`rrRealized`), not a dollar fills ledger. The
 * page labels every such figure "R" so the reader is never shown a fake dollar number.
 */
import type { DecisionRecord, EquityPoint, TradeRecord } from '../api/types';

// ── headline KPIs ──────────────────────────────────────────────────────────────

export interface TradeKpis {
  readonly total: number;
  readonly wins: number;
  readonly losses: number;
  readonly breakeven: number;
  /** wins / (wins+losses); null when nothing has resolved to a win or loss yet. */
  readonly winrate: number | null;
  /** gross profit R ÷ gross loss R; null when there is no loss (or no data) to divide by. */
  readonly profitFactor: number | null;
  /** Σ realized R across every closed trade. */
  readonly netR: number;
  /** mean realized R per closed trade; null when there are no trades. */
  readonly avgR: number | null;
  readonly grossProfitR: number;
  readonly grossLossR: number;
}

export function tradeKpis(trades: readonly TradeRecord[]): TradeKpis {
  let wins = 0;
  let losses = 0;
  let breakeven = 0;
  let grossProfitR = 0;
  let grossLossR = 0; // positive magnitude
  let netR = 0;
  let rCount = 0;

  for (const t of trades) {
    if (t.result === 'WIN') wins += 1;
    else if (t.result === 'LOSS') losses += 1;
    else breakeven += 1;
    if (t.rrRealized != null) {
      netR += t.rrRealized;
      rCount += 1;
      if (t.rrRealized > 0) grossProfitR += t.rrRealized;
      else if (t.rrRealized < 0) grossLossR += -t.rrRealized;
    }
  }

  const decided = wins + losses;
  return {
    total: trades.length,
    wins,
    losses,
    breakeven,
    winrate: decided > 0 ? wins / decided : null,
    profitFactor: grossLossR > 0 ? grossProfitR / grossLossR : null,
    netR,
    avgR: rCount > 0 ? netR / rCount : null,
    grossProfitR,
    grossLossR,
  };
}

// ── P&L over time (per-day bars + cumulative line, both in R) ────────────────────

export interface DayPnl {
  /** UTC calendar day, YYYY-MM-DD. */
  readonly day: string;
  readonly r: number;
  readonly cumR: number;
  readonly trades: number;
}

/** Bucket realized R by UTC day, oldest-first, with a running cumulative total. */
export function dailyPnl(trades: readonly TradeRecord[]): DayPnl[] {
  const byDay = new Map<string, { r: number; trades: number }>();
  for (const t of trades) {
    if (t.rrRealized == null) continue;
    const day = t.createdAt.slice(0, 10); // ISO → YYYY-MM-DD (UTC)
    const cell = byDay.get(day) ?? { r: 0, trades: 0 };
    cell.r += t.rrRealized;
    cell.trades += 1;
    byDay.set(day, cell);
  }
  const days = [...byDay.keys()].sort();
  let cum = 0;
  return days.map((day) => {
    const cell = byDay.get(day)!;
    cum += cell.r;
    return { day, r: cell.r, cumR: cum, trades: cell.trades };
  });
}

// ── R-multiple distribution histogram ────────────────────────────────────────────

export interface HistoBin {
  readonly x0: number;
  readonly x1: number;
  readonly count: number;
  readonly label: string;
}

/** Fixed 0.5R-wide bins spanning the observed range, centered so a bin edge sits on 0R. */
export function rHistogram(trades: readonly TradeRecord[], width = 0.5): HistoBin[] {
  const rs = trades.map((t) => t.rrRealized).filter((r): r is number => r != null);
  if (rs.length === 0) return [];
  const lo = Math.floor(Math.min(...rs) / width) * width;
  const hi = Math.ceil(Math.max(...rs) / width) * width;
  const bins: HistoBin[] = [];
  for (let x0 = lo; x0 < hi + 1e-9; x0 += width) {
    const x1 = x0 + width;
    const count = rs.filter((r) => r >= x0 - 1e-9 && (r < x1 - 1e-9 || (x1 >= hi && r <= x1 + 1e-9))).length;
    bins.push({ x0, x1, count, label: `${fmtR(x0)}…${fmtR(x1)}` });
  }
  return bins;
}

function fmtR(v: number): string {
  const s = Math.abs(v).toFixed(1);
  return `${v < 0 ? '−' : ''}${s}`;
}

// ── generic value histogram (used for committee-confidence distributions) ────────

/**
 * Bin values into `binCount` equal buckets across [min,max]. Confidence is stored either
 * as a fraction (0..1) or a percentage (0..100) depending on the source; we normalise to
 * 0..100 first so the axis is stable regardless of which the backend sends.
 */
export function confidenceHistogram(values: readonly number[], binCount = 10): HistoBin[] {
  if (values.length === 0) return [];
  const norm = values.map((v) => (v <= 1 ? v * 100 : v));
  const width = 100 / binCount;
  const bins: HistoBin[] = [];
  for (let i = 0; i < binCount; i += 1) {
    const x0 = i * width;
    const x1 = x0 + width;
    const count = norm.filter((v) => v >= x0 && (v < x1 || (i === binCount - 1 && v <= x1))).length;
    bins.push({ x0, x1, count, label: `${x0.toFixed(0)}–${x1.toFixed(0)}%` });
  }
  return bins;
}

// ── one-dimension breakdown (per-symbol / per-setup) ─────────────────────────────

export type BreakdownMetric = 'winrate' | 'avgR' | 'netR';

export interface BreakdownRow {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly n: number;
}

/** Group trades by a categorical field and compute winrate / avg R / net R per group. */
export function breakdownBy(
  trades: readonly TradeRecord[],
  pick: (t: TradeRecord) => string,
  metric: BreakdownMetric,
): BreakdownRow[] {
  const groups = new Map<string, TradeRecord[]>();
  for (const t of trades) {
    const key = pick(t) || '—';
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(t);
  }
  const out: BreakdownRow[] = [];
  for (const [key, rows] of groups) {
    const n = rows.length;
    let value: number;
    if (metric === 'winrate') {
      const decided = rows.filter((r) => r.result === 'WIN' || r.result === 'LOSS').length;
      value = decided > 0 ? rows.filter((r) => r.result === 'WIN').length / decided : 0;
    } else {
      const rs = rows.map((r) => r.rrRealized).filter((r): r is number => r != null);
      const sum = rs.reduce((a, r) => a + r, 0);
      value = metric === 'netR' ? sum : rs.length > 0 ? sum / rs.length : 0;
    }
    out.push({ key, label: prettyLabel(key), value, n });
  }
  return out.sort((a, b) => b.n - a.n);
}

// ── MAE / MFE scatter ────────────────────────────────────────────────────────────

export interface ScatterPoint {
  readonly mae: number;
  readonly mfe: number;
  readonly r: number;
  readonly result: TradeRecord['result'];
  readonly symbol: string;
  readonly setup: string;
}

/** Trades with both excursions recorded, as (MAE, MFE) points coloured by outcome. */
export function excursionScatter(trades: readonly TradeRecord[]): ScatterPoint[] {
  const out: ScatterPoint[] = [];
  for (const t of trades) {
    if (t.mae == null || t.mfe == null) continue;
    out.push({
      mae: Math.abs(t.mae),
      mfe: Math.abs(t.mfe),
      r: t.rrRealized ?? 0,
      result: t.result,
      symbol: t.symbol,
      setup: t.setupType,
    });
  }
  return out;
}

// ── equity drawdown (underwater) ─────────────────────────────────────────────────

export interface DrawdownPoint {
  readonly at: number;
  /** Fraction below the running peak, ≤ 0 (0 at a fresh high). */
  readonly dd: number;
}

export interface DrawdownSeries {
  readonly points: readonly DrawdownPoint[];
  /** Worst (most negative) drawdown fraction over the window. */
  readonly maxDrawdown: number;
}

export function drawdown(points: readonly EquityPoint[]): DrawdownSeries {
  let peak = -Infinity;
  let worst = 0;
  const out: DrawdownPoint[] = [];
  for (const p of points) {
    peak = Math.max(peak, p.equity);
    const dd = peak > 0 ? (p.equity - peak) / peak : 0;
    if (dd < worst) worst = dd;
    out.push({ at: p.at, dd });
  }
  return { points: out, maxDrawdown: worst };
}

// ── decision funnel + no-trade reasons + committee confidence ───────────────────

export interface FunnelStage {
  readonly key: string;
  readonly label: string;
  readonly count: number;
}

/** True when a decision reached the full committee (more than the lone Signal vote). */
function ranCommittee(d: DecisionRecord): boolean {
  const roles = Object.keys(d.votes ?? {});
  return roles.length > 1 || roles.some((r) => r !== 'signal');
}

export function decisionFunnel(decisions: readonly DecisionRecord[]): FunnelStage[] {
  const total = decisions.length;
  const committee = decisions.filter(ranCommittee).length;
  const enter = decisions.filter((d) => d.verdict === 'ENTER').length;
  return [
    { key: 'candidates', label: 'Candidates screened', count: total },
    { key: 'committee', label: 'Full committee ran', count: committee },
    { key: 'enter', label: 'ENTER', count: enter },
  ];
}

export interface ReasonRow {
  readonly key: string;
  readonly label: string;
  readonly count: number;
}

/**
 * Why the desk did NOT enter, tallied across every non-ENTER decision: a failed gate is
 * named by its gate; a short-circuit (Signal saw no setup) is its own bucket; a full
 * committee that simply declined with no failing gate falls to "Arbiter declined".
 */
export function noTradeReasons(decisions: readonly DecisionRecord[]): ReasonRow[] {
  const tally = new Map<string, number>();
  const bump = (k: string): void => { tally.set(k, (tally.get(k) ?? 0) + 1); };
  for (const d of decisions) {
    if (d.verdict === 'ENTER') continue;
    const failed = (d.gateResults ?? []).filter((g) => !g.pass);
    if (failed.length > 0) {
      for (const g of failed) bump(`gate:${g.gate}`);
    } else if (!ranCommittee(d)) {
      bump('signal:no-setup');
    } else {
      bump('arbiter:declined');
    }
  }
  return [...tally.entries()]
    .map(([key, count]) => ({ key, label: prettyReason(key), count }))
    .sort((a, b) => b.count - a.count);
}

/** Confidence values (as sent by the backend) for a given committee role across decisions. */
export function roleConfidence(decisions: readonly DecisionRecord[], role: string): number[] {
  const out: number[] = [];
  for (const d of decisions) {
    const c = d.votes?.[role]?.confidence;
    if (typeof c === 'number' && Number.isFinite(c)) out.push(c);
  }
  return out;
}

// ── labels ───────────────────────────────────────────────────────────────────────

function prettyLabel(value: string): string {
  if (/x$/i.test(value)) return value.replace(/x$/i, '×');
  const s = value.replace(/[-_]/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function prettyReason(key: string): string {
  if (key === 'signal:no-setup') return 'Signal: no setup';
  if (key === 'arbiter:declined') return 'Arbiter declined';
  const gate = key.replace(/^gate:/, '');
  return `Gate: ${prettyLabel(gate)}`;
}
